import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { FileText, Shield, Cloud, Wallet, Wifi, CheckCircle, ExternalLink } from "lucide-react";
import SEO, { pageSEO } from "../components/SEO";

export default function TravelPrepPage() {
  return (
    <div className="pt-24 md:pt-32" data-testid="travel-prep-page">
      <SEO {...pageSEO.travelPrep} />
      {/* Hero */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Be Prepared</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            Travel <span className="text-[#D4AF37]">Prep</span>
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            Practical information to help you prepare for an amazing India experience
          </p>
        </div>
      </section>

      {/* Visa Section */}
      <section className="section-padding bg-white" data-testid="visa-section">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#243b5a]/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-[#243b5a]" />
              </div>
              <h2 className="font-serif text-3xl text-[#243b5a]">Visas & Entry Requirements</h2>
            </div>
            
            <div className="prose prose-lg text-[#4A4A4A] space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>U.S. citizens need a valid visa to enter India. The e-Visa is the most convenient option.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Apply online at the official Indian government e-Visa portal (indianvisaonline.gov.in).</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Processing typically takes 3-5 business days. We recommend applying 4-6 weeks before departure.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Your passport must be valid for at least 6 months beyond your travel dates with at least 2 blank pages.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>e-Visa allows stays up to 30 days, which is more than sufficient for our 10-day journey.</span>
                </li>
              </ul>
            </div>
            
            <a 
              href="https://indianvisaonline.gov.in" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-6 text-[#243b5a] font-medium hover:text-[#D4AF37] transition-colors"
            >
              Official e-Visa Portal <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Health & Safety */}
      <section className="section-padding bg-[#F9F9F7]" data-testid="health-section">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#243b5a]/10 flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#243b5a]" />
              </div>
              <h2 className="font-serif text-3xl text-[#243b5a]">Health & Safety</h2>
            </div>
            
            <div className="prose prose-lg text-[#4A4A4A] space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Consult your doctor or a travel medicine clinic 4-6 weeks before departure.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Recommended vaccinations may include Hepatitis A & B, Typhoid, and routine vaccinations. Your doctor will advise based on your medical history.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>We select restaurants with high hygiene standards. Bottled water is always provided.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Avoid tap water, ice in drinks at street vendors, and raw unpeeled fruits/vegetables.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Pack any prescription medications with copies of prescriptions. A basic travel health kit (antidiarrheal, pain reliever, etc.) is wise.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Our hotels all have 24-hour medical assistance available.</span>
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 mt-8 border border-[#D4AF37]/20">
              <h3 className="font-serif text-xl text-[#243b5a] mb-4">Quick Tips</h3>
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-xs text-[#D4AF37] font-medium">1</span>
                  Stay hydrated — India can be warm
                </li>
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-xs text-[#D4AF37] font-medium">2</span>
                  Use hand sanitizer frequently
                </li>
                <li className="flex items-center gap-2 text-[#4A4A4A]">
                  <span className="w-6 h-6 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-xs text-[#D4AF37] font-medium">3</span>
                  Eat only freshly prepared, hot food
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Weather & Packing */}
      <section className="section-padding bg-white" data-testid="weather-section">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#243b5a]/10 flex items-center justify-center">
                <Cloud className="w-6 h-6 text-[#243b5a]" />
              </div>
              <h2 className="font-serif text-3xl text-[#243b5a]">Weather & Packing</h2>
            </div>
            
            <div className="prose prose-lg text-[#4A4A4A] space-y-4 mb-8">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>October-March is peak season with pleasant temperatures (60-80°F/15-27°C in most areas).</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>The Himalayas will be cooler — expect 40-60°F (5-15°C). Layering is essential.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Comfortable walking shoes are a must — you'll be on your feet at monuments.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Modest dress is recommended for temple visits (cover shoulders and knees).</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Sun protection: hat, sunglasses, sunscreen (especially at the Taj Mahal).</span>
                </li>
              </ul>
            </div>

            <div className="bg-[#F9F9F7] rounded-xl p-6 border border-[#D4AF37]/20">
              <h3 className="font-serif text-xl text-[#243b5a] mb-4">Packing Essentials</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  "Light, breathable layers (cotton, linen)",
                  "One warm jacket or fleece for Himalayas",
                  "Comfortable walking shoes (broken in!)",
                  "Scarf/shawl for temple visits",
                  "Sunhat, sunglasses, sunscreen",
                  "Small daypack for sightseeing",
                  "Camera and chargers",
                  "Universal power adapter (India uses Type C/D plugs)"
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-[#4A4A4A]">
                    <CheckCircle className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Money & Tipping */}
      <section className="section-padding bg-[#F9F9F7]" data-testid="money-section">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#243b5a]/10 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-[#243b5a]" />
              </div>
              <h2 className="font-serif text-3xl text-[#243b5a]">Money & Tipping</h2>
            </div>
            
            <div className="prose prose-lg text-[#4A4A4A] space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Indian Rupee (INR) is the local currency. Current exchange: approximately $1 = ₹89-90.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Most upscale establishments accept credit cards. Carry some cash for markets and tips.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>ATMs are widely available. Inform your bank of travel dates to avoid card blocks.</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-white rounded-lg border border-[#D4AF37]/20">
                <p className="font-medium text-[#243b5a] mb-2">Tipping Guidelines:</p>
                <ul className="space-y-1 text-sm">
                  <li><strong>Hotel porters:</strong> ₹100 per bag</li>
                  <li><strong>Restaurant servers:</strong> 10% if service not included</li>
                  <li><strong>Drivers/guides:</strong> We'll provide specific recommendations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Connectivity */}
      <section className="section-padding bg-white" data-testid="connectivity-section">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-14 h-14 rounded-full bg-[#243b5a]/10 flex items-center justify-center">
                <Wifi className="w-6 h-6 text-[#243b5a]" />
              </div>
              <h2 className="font-serif text-3xl text-[#243b5a]">Connectivity</h2>
            </div>
            
            <div className="prose prose-lg text-[#4A4A4A] space-y-4">
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>All our hotels have complimentary Wi-Fi. Coverage is generally good throughout.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Consider purchasing a local SIM card at Delhi airport for data/calls (~$15-18 for the trip).</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Alternatively, check with your U.S. carrier for international plans.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>WhatsApp is widely used in India — great for communicating with Aaron and guides.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-1 flex-shrink-0" />
                  <span>Download offline maps (Google Maps) before departure as a backup.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Receive */}
      <section className="section-padding bg-[#243b5a] text-white">
        <div className="container-max text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            You'll <span className="text-[#D4AF37]">Receive</span>
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Once you've reserved your spot, we'll send a comprehensive pre-departure package including:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              "Detailed packing list specific to your departure dates",
              "Step-by-step visa application guide with screenshots",
              "Flight recommendations from NYC/NJ airports",
              "Travel insurance options and recommendations",
              "Cultural etiquette guide and useful Hindi phrases"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/10 p-4 rounded-lg">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white/90 text-sm text-left">{item}</span>
              </div>
            ))}
          </div>
          <Link to="/contact" className="inline-block mt-10">
            <Button className="btn-gold" data-testid="start-journey-travel-prep-btn">
              Start Your Journey
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
